import {
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { Moon, Sun } from 'lucide-react';
import { useContext , useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icon } from '@/components/common/Icon';
import { UserAvatar } from '@/components/common/UserAvatar';
import { AppThemeContext } from '@/context/AppThemeProvider';
import { useAuthContext } from '@/context/AuthProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserProfile } from '@/hooks/user/useUserProfile';
import { useUserRole } from '@/hooks/useUserRole';
import { Mode } from '@/theme';

// ─── Engagement period helpers ──────────────────────────────────────────────
const PERIOD_DAYS_MAP: Record<string, number> = {
  Initial: 90,
  'Ext I': 90,
  'Ext II': 90,
  'Ext III': 90,
};
const URGENCY_COLORS = {
  low: '#5AE9FF',
  moderate: '#FFF981',
  urgent: '#FF9A4D',
  critical: '#DB5282',
};

function getDealEngagementInfo(engagementStart: string, periodType: string) {
  if (!engagementStart) return null;
  const start = new Date(engagementStart + 'T00:00:00');
  const totalDays = PERIOD_DAYS_MAP[periodType] || 90;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const elapsed = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const days = Math.min(totalDays - Math.max(0, elapsed), totalDays);
  const progress = Math.max(0, Math.min(1, elapsed / totalDays));

  let color: string;
  if (days > 60) color = URGENCY_COLORS.low;
  else if (days > 30) color = URGENCY_COLORS.moderate;
  else if (days > 14) color = URGENCY_COLORS.urgent;
  else color = URGENCY_COLORS.critical;

  return { days: Math.max(0, days), progress, color, expired: days <= 0 };
}

/**
 * UserSwitcher component provides an avatar dropdown with Settings and Logout.
 */
export const UserSwitcher = ({
  collapsed = false,
  showEngagement = false,
}: {
  collapsed?: boolean;
  showEngagement?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { user, logOut } = useAuthContext();
  const { isAdmin, isSuperAdmin, isAdvisor, canAccessAdmin } = useUserRole();
  const { userProfile } = useUserProfile();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const { selectedProperty } = usePropertiesContext();

  // Self-serve: signed up directly (no advisor invite), so never a "Buyer" —
  // same check ClientSidebar uses to give this persona its own nav.
  const isIndependentOwner = !isLoadingGroups && groups.length === 0;

  const engagements = useMemo(() => {
    const result: {
      dealName: string;
      days: number;
      progress: number;
      color: string;
      expired: boolean;
    }[] = [];
    for (const group of groups) {
      if (group.status !== 'active') continue;
      for (const deal of group.deals) {
        if (deal.status === 'Closed') continue;
        const info = getDealEngagementInfo(deal.engagementStart, deal.periodType);
        if (info) result.push({ dealName: deal.name, ...info });
      }
    }
    result.sort((a, b) => a.days - b.days);
    return result;
  }, [groups]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    handleClose();
    navigate(canAccessAdmin ? '/admin/settings' : '/dashboard/settings');
  };

  const handleLogout = async () => {
    handleClose();
    await logOut();
    navigate('/login');
  };

  const displayName = user?.name || 'User';

  // Photo URL for the current user's avatar.
  // Append updatedAt to bust the browser cache when the user re-uploads with the same S3 key.
  const currentUserPhotoUrl = userProfile?.photoUrl
    ? `${userProfile.photoUrl}?v=${userProfile.updatedAt}`
    : undefined;

  // the account menu floats: the theme's dialog surface with a small shadow
  // Day / Night lives here, at the top of the account menu (optional: the menu also renders without the provider)
  const appTheme = useContext(AppThemeContext);
  // Day / Night as one sun-and-moon switch: a Tradehaus rounded-square track with both glyphs, the square thumb sliding
  // under the one that's on (Day lights the chalk ground; Night is the dark room)
  const appearance = appTheme ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: '4px 6px 10px',
        mb: '6px',
        borderBottom: '1px solid var(--tx-line)',
      }}>
      <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-muted)' }}>
        Backlight
      </Box>
      <ModeSwitch mode={appTheme.mode} onChange={appTheme.setMode} />
    </Box>
  ) : null;

  const menuPaperSx = {
    maxHeight: 440,
    backgroundColor: 'var(--tx-dialog)',
    backgroundImage: 'var(--tx-surface-light)',
    boxShadow: 'var(--tx-shadow-sm)',
    borderRadius: '16px',
    p: '8px',
  };

  const menuItemSx = {
    '& .MuiList-root': { p: 0 },
    '& .MuiMenuItem-root': {
      px: '12px !important',
      py: '10px !important',
      minHeight: 'unset !important',
      borderRadius: '12px',
      mb: '1px',
      transition: 'background 0.15s',
      '&:hover': { backgroundColor: 'var(--tx-fill) !important' },
    },
    '& .MuiListItemIcon-root': { minWidth: 30, color: 'var(--tx-muted)' },
    '& .MuiListItemText-primary': {
      fontFamily: 'Manrope, sans-serif',
      fontWeight: '500 !important',
      fontSize: '13px !important',
      letterSpacing: '-0.01em',
      color: 'var(--tx-text) !important',
    },
    '& .MuiListItemText-secondary': {
      fontFamily: 'Manrope, sans-serif',
      fontSize: '12px !important',
      color: 'var(--tx-muted) !important',
    },
  };

  if (collapsed) {
    return (
      <>
        <Tooltip title={displayName} placement="right">
          <Box
            onClick={handleClick}
            data-testid="user-switcher-trigger"
            sx={{
              pt: '8px',
              pb: '20px',
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
            }}>
            <UserAvatar
              name={displayName}
              photoUrl={currentUserPhotoUrl}
              size={28}
              lastActiveAt={userProfile?.lastActiveAt}
            />
          </Box>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          marginThreshold={8}
          slotProps={{
            paper: {
              sx: { ml: '10px', width: 260, ...menuPaperSx },
            },
          }}
          sx={menuItemSx}>
          {appearance}
          <MenuItem onClick={handleSettings} data-testid="user-menu-settings">
            <ListItemIcon>
              <Icon name="gear" size={16} />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{ variant: 'body2', fontSize: '0.875rem', fontWeight: 300 }}
            />
          </MenuItem>
          <MenuItem onClick={handleLogout} data-testid="user-menu-logout">
            <ListItemIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ variant: 'body2', fontSize: '0.875rem', fontWeight: 300 }}
            />
          </MenuItem>
        </Menu>
      </>
    );
  }

  return (
    <>
      <Box
        onClick={handleClick}
        data-testid="user-switcher-trigger"
        sx={{
          width: '100%',
          mx: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: '10px',
          py: '8px',
          borderRadius: '12px',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': {
            backgroundColor: 'var(--tx-fill)',
          },
        }}>
        <UserAvatar
          name={displayName}
          photoUrl={currentUserPhotoUrl}
          size={32}
          lastActiveAt={userProfile?.lastActiveAt}
          sx={{ bgcolor: '#444444' }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '3px',
          }}>
          <Typography
            noWrap
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: 13,
              color: 'var(--tx-text)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
            }}>
            {displayName}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--tx-muted)',
              lineHeight: 1,
            }}>
            {isSuperAdmin
              ? 'Super Admin'
              : isAdmin
              ? 'Admin'
              : isAdvisor
              ? 'Advisor'
              : isIndependentOwner
              ? 'Homeowner'
              : selectedProperty?.isOwned
              ? 'Owner'
              : 'Buyer'}
          </Typography>
        </Box>
        <UnfoldMoreIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: -10, horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        marginThreshold={0}
        slotProps={{
          paper: {
            sx: {
              ...menuPaperSx,
              width: anchorEl ? anchorEl.offsetWidth : 260,
              ml: 0,
            },
          },
        }}
        sx={{
          ...menuItemSx,
          '& .MuiMenuItem-root': {
            ...menuItemSx['& .MuiMenuItem-root'],
            borderBottomWidth: '0 !important',
            lineHeight: '1.4 !important',
          },
        }}>
        {/* Engagement countdown bars */}
        {showEngagement && engagements.length > 0 && (
          <Box
            sx={{
              px: '12px',
              pt: '4px',
              pb: '10px',
              mb: '4px',
              borderBottom: '1px solid var(--tx-line)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
            {engagements.map((e, i) => (
              <Box key={i}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: '5px',
                  }}>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--tx-faint)',
                      flex: 1,
                      minWidth: 0,
                    }}>
                    {e.dealName}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: 10,
                      fontWeight: 600,
                      color: e.color,
                      letterSpacing: '0.01em',
                      flexShrink: 0,
                      ml: '8px',
                    }}>
                    {e.expired ? 'Expired' : `${e.days}d`}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 3,
                    borderRadius: 2,
                    backgroundColor: 'var(--tx-fill-2)',
                    overflow: 'hidden',
                  }}>
                  <Box
                    sx={{
                      width: `${e.progress * 100}%`,
                      height: '100%',
                      borderRadius: 2,
                      backgroundColor: e.color,
                      transition: 'width 0.4s ease, background-color 0.4s ease',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Settings option */}
        {appearance}
          <MenuItem onClick={handleSettings} data-testid="user-menu-settings">
          <ListItemIcon>
            <Icon name="gear" size={16} />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{ variant: 'body2', fontSize: '0.875rem' }}
          />
        </MenuItem>

        {/* Logout option */}
        <MenuItem onClick={handleLogout} data-testid="user-menu-logout">
          <ListItemIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ variant: 'body2', fontSize: '0.875rem' }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

// Day / Night as two hairline glyphs, a sun and a moon, divided by a fine vertical rule: the one that's on in ink, the
// other faint until hovered. No track, no thumb; just the two choices
export const ModeSwitch = ({ mode, onChange }: { mode: Mode; onChange: (mode: Mode) => void }) => (
  <Box role="radiogroup" aria-label="Backlight" sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
    {([
      ['day', Sun, 'Day'],
      ['night', Moon, 'Night'],
    ] as const).map(([m, Glyph, label], i) => (
      <Box key={m} sx={{ display: 'flex', alignItems: 'center' }}>
        {i > 0 && <Box aria-hidden="true" sx={{ width: '1px', height: 14, mx: '6px', backgroundColor: 'var(--tx-line)' }} />}
        <Tooltip title={label} placement="top">
          <ButtonBase
            role="radio"
            aria-checked={mode === m}
            aria-label={label}
            onClick={() => onChange(m)}
            sx={{
              width: 26,
              height: 26,
              borderRadius: '7px',
              color: mode === m ? 'var(--tx-text)' : 'var(--tx-faint)',
              transition: 'color 0.15s ease',
              '&:hover': { color: 'var(--tx-text)' },
            }}>
            <Glyph size={15} />
          </ButtonBase>
        </Tooltip>
      </Box>
    ))}
  </Box>
);
